<script setup>
import { reactive, ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert as showAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WhatsAppPhonePreview from '../components/WhatsAppPhonePreview.vue';
import MessageSequenceBuilder from '../components/MessageSequenceBuilder.vue';
import CampaignsAPI from 'dashboard/api/campaigns';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const currentStep = ref(0);
const isSubmitting = ref(false);
const isLoadingCampaign = ref(false);

const labels = useMapGetter('labels/getLabels');
const funnels = useMapGetter('funnels/getFunnels');

const accountId = computed(() => route.params.accountId);

// Edit mode detection
const editCampaignId = computed(() => {
  const id = route.query.campaignId;
  return id ? Number(id) : null;
});
const isEditMode = computed(() => editCampaignId.value !== null);

// Access inbox state directly from Vuex store state (most reliable)
const isLoadingInboxes = computed(
  () => store.state.inboxes?.uiFlags?.isFetching ?? false
);

// Known WhatsApp providers for API-based inboxes
const WHATSAPP_PROVIDERS = [
  'evolution',
  'waha',
  'uazapi',
  'baileys',
  'notificame',
  'whatsapp_cloud',
];

const isWhatsAppApiInbox = item => {
  const attrs = item.additional_attributes || {};
  // Check channel_identifier attribute
  if (attrs.channel_identifier === 'whatsapp') return true;
  // Check provider field (top-level or inside additional_attributes)
  const provider = (item.provider || attrs.provider || '').toLowerCase();
  if (WHATSAPP_PROVIDERS.includes(provider)) return true;
  // Fallback: inbox name contains "whatsapp"
  if (item.name?.toLowerCase().includes('whatsapp')) return true;
  return false;
};

const whatsappInboxes = computed(() => {
  const records = store.state.inboxes?.records || [];
  return records.filter(item => {
    const ct = item.channel_type;
    if (ct === 'Channel::Whatsapp') return true;
    if (ct === 'Channel::TwilioSms' && item.medium === 'whatsapp') return true;
    if (ct === 'Channel::Api' && isWhatsAppApiInbox(item)) return true;
    return false;
  });
});

const refreshInboxes = async () => {
  await store.dispatch('inboxes/get');
};

// Form state
const form = reactive({
  title: '',
  description: '',
  // Message sequence (array of { type, content } objects)
  sequence: [],
  // Audience
  audienceType: 'tags',
  selectedAudience: [],
  selectedFunnel: null,
  selectedStage: null,
  // Multi-inbox
  selectedInboxIds: [],
  // Schedule
  scheduleType: 'now',
  scheduledAt: null,
  recurrenceType: null,
  recurrenceCron: '',
  // Anti-ban
  sendingSpeed: 'normal',
  minDelaySeconds: 15,
  maxDelaySeconds: 45,
  businessHoursOnly: true,
  businessHoursStart: 8,
  businessHoursEnd: 20,
  coffeeBreakEnabled: true,
  coffeeBreakEvery: 15,
  maxMessagesPerHourPerInbox: 80,
  allowedDays: [1, 2, 3, 4, 5],
  validateNumbers: false,
  trackLinks: false,
  // Target funnel assignment
  targetFunnelEnabled: false,
  targetFunnelId: null,
  targetFunnelStage: null,
});

// 6-step wizard: Basic → Inboxes → Message → Audience → Settings → Review
const steps = computed(() => [
  {
    key: 'basic',
    label: t('CAMPAIGN.WIZARD.STEPS.BASIC'),
    icon: 'i-lucide-file-text',
  },
  {
    key: 'inboxes',
    label: t('CAMPAIGN.WIZARD.STEPS.INBOXES'),
    icon: 'i-lucide-inbox',
  },
  {
    key: 'message',
    label: t('CAMPAIGN.WIZARD.STEPS.MESSAGE'),
    icon: 'i-lucide-message-square',
  },
  {
    key: 'audience',
    label: t('CAMPAIGN.WIZARD.STEPS.AUDIENCE'),
    icon: 'i-lucide-users',
  },
  {
    key: 'settings',
    label: t('CAMPAIGN.WIZARD.STEPS.SETTINGS'),
    icon: 'i-lucide-settings',
  },
  {
    key: 'review',
    label: t('CAMPAIGN.WIZARD.STEPS.REVIEW'),
    icon: 'i-lucide-check-circle',
  },
]);

// Derived message text from sequence (backward compat)
const derivedMessage = computed(() => {
  // For templates, use the body text from the template data
  const tmplItem = form.sequence.find(i => i.type === 'template');
  if (tmplItem?.content?.templateData) {
    const body = (tmplItem.content.templateData.components || []).find(
      c => c.type === 'BODY'
    );
    if (body?.text) return body.text;
  }
  // Fallback for text items
  return form.sequence
    .filter(item => item.type === 'text')
    .map(item => item.content?.text || '')
    .filter(Boolean)
    .join('\n\n');
});

// Message count (non-wait items)
const messageCount = computed(
  () => form.sequence.filter(item => item.type !== 'wait').length
);

// Inbox helpers
const selectedInboxObjects = computed(() =>
  (whatsappInboxes.value || []).filter(i =>
    form.selectedInboxIds.includes(i.id)
  )
);

const primaryInbox = computed(() => selectedInboxObjects.value[0] || null);

const isMultiInbox = computed(() => form.selectedInboxIds.length > 1);

const getProviderLabel = inbox => {
  const provider =
    inbox?.provider ||
    inbox?.additional_attributes?.provider ||
    inbox?.channel?.provider ||
    '';
  const map = {
    default: 'WhatsApp',
    evolution: 'Evolution',
    waha: 'WAHA',
    whatsapp_cloud: 'Cloud API',
    notificame: 'HubNavi (API Oficial)',
    uazapi: 'Uazapi',
    baileys: 'Baileys',
  };
  if (map[provider]) return map[provider];
  // For API-based WhatsApp inboxes without explicit provider
  if (inbox?.channel_type === 'Channel::Api') {
    return inbox?.additional_attributes?.channel_name || 'API WhatsApp';
  }
  return provider || 'WhatsApp';
};

const getProviderColor = inbox => {
  const attrs = inbox?.additional_attributes || {};
  const provider = inbox?.provider || attrs.provider || '';
  if (['evolution', 'waha', 'uazapi', 'baileys'].includes(provider)) {
    return 'bg-purple-500/10 text-purple-600';
  }
  if (['whatsapp_cloud', 'notificame'].includes(provider)) {
    return 'bg-green-500/10 text-green-600';
  }
  if (inbox?.channel_type === 'Channel::Api') {
    return 'bg-blue-500/10 text-blue-600';
  }
  return 'bg-green-500/10 text-green-600';
};

// Audience helpers
const audienceTypeOptions = computed(() => [
  {
    value: 'tags',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE_TYPE.TAGS'),
  },
  {
    value: 'funnel',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE_TYPE.FUNNEL'),
  },
]);

const labelOptions = computed(() =>
  (labels.value || []).map(l => ({ value: l.id, label: l.title }))
);

const funnelOptions = computed(() =>
  (funnels.value || []).map(f => ({ value: f.id, label: f.name }))
);

const stageOptions = computed(() => {
  if (!form.selectedFunnel) return [];
  const funnel = (funnels.value || []).find(f => f.id === form.selectedFunnel);
  if (!funnel?.stages) return [];
  return Object.entries(funnel.stages)
    .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
    .map(([key, stage]) => ({ value: key, label: stage.label || key }));
});

const targetStageOptions = computed(() => {
  if (!form.targetFunnelId) return [];
  const funnel = (funnels.value || []).find(f => f.id === form.targetFunnelId);
  if (!funnel?.stages) return [];
  return Object.entries(funnel.stages)
    .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
    .map(([key, stage]) => ({ value: key, label: stage.label || key }));
});

const audienceCount = computed(() => {
  if (form.audienceType === 'tags') return form.selectedAudience.length;
  return form.selectedFunnel && form.selectedStage ? 1 : 0;
});

// Sending speed presets
const sendingSpeedOptions = computed(() => [
  {
    value: 'slow',
    label: t('CAMPAIGN.WIZARD.ANTI_BAN.SPEED_SLOW'),
    desc: t('CAMPAIGN.WIZARD.ANTI_BAN.SPEED_SLOW_DESC'),
    icon: 'i-lucide-shield',
  },
  {
    value: 'normal',
    label: t('CAMPAIGN.WIZARD.ANTI_BAN.SPEED_NORMAL'),
    desc: t('CAMPAIGN.WIZARD.ANTI_BAN.SPEED_NORMAL_DESC'),
    icon: 'i-lucide-zap',
  },
  {
    value: 'fast',
    label: t('CAMPAIGN.WIZARD.ANTI_BAN.SPEED_FAST'),
    desc: t('CAMPAIGN.WIZARD.ANTI_BAN.SPEED_FAST_DESC'),
    icon: 'i-lucide-rocket',
  },
  {
    value: 'custom',
    label: t('CAMPAIGN.WIZARD.ANTI_BAN.SPEED_CUSTOM'),
    desc: t('CAMPAIGN.WIZARD.ANTI_BAN.SPEED_CUSTOM_DESC'),
    icon: 'i-lucide-sliders-horizontal',
  },
]);

const updateDelaysFromSpeed = speed => {
  const presets = {
    slow: { min: 30, max: 90, perHour: 40 },
    normal: { min: 15, max: 45, perHour: 80 },
    fast: { min: 5, max: 15, perHour: 200 },
  };
  const p = presets[speed];
  if (p) {
    form.minDelaySeconds = p.min;
    form.maxDelaySeconds = p.max;
    form.maxMessagesPerHourPerInbox = p.perHour;
  }
};

const daysOfWeek = computed(() => [
  { value: 0, label: t('CAMPAIGN.WIZARD.ANTI_BAN.DAYS.SUN') },
  { value: 1, label: t('CAMPAIGN.WIZARD.ANTI_BAN.DAYS.MON') },
  { value: 2, label: t('CAMPAIGN.WIZARD.ANTI_BAN.DAYS.TUE') },
  { value: 3, label: t('CAMPAIGN.WIZARD.ANTI_BAN.DAYS.WED') },
  { value: 4, label: t('CAMPAIGN.WIZARD.ANTI_BAN.DAYS.THU') },
  { value: 5, label: t('CAMPAIGN.WIZARD.ANTI_BAN.DAYS.FRI') },
  { value: 6, label: t('CAMPAIGN.WIZARD.ANTI_BAN.DAYS.SAT') },
]);

const toggleDay = day => {
  const idx = form.allowedDays.indexOf(day);
  if (idx > -1) form.allowedDays.splice(idx, 1);
  else {
    form.allowedDays.push(day);
    form.allowedDays.sort();
  }
};

// Derive AI enabled from sequence (AI is now a message type, not a separate step)
const aiFromSequence = computed(() => {
  const aiItem = form.sequence.find(item => item.type === 'ai');
  return aiItem
    ? {
        enabled: true,
        prompt: aiItem.content?.userPrompt || '',
        systemPrompt: aiItem.content?.systemPrompt || '',
        tone: aiItem.content?.tone || 'friendly',
        useEmojis: aiItem.content?.useEmojis ?? true,
        includeGreeting: aiItem.content?.includeGreeting ?? true,
        maxLength: aiItem.content?.maxLength || 500,
      }
    : { enabled: false };
});

// Validation per step (Basic → Inboxes → Message → Audience → Settings → Review)
const canProceed = computed(() => {
  switch (currentStep.value) {
    case 0:
      return form.title.trim().length > 0;
    case 1:
      return form.selectedInboxIds.length > 0;
    case 2:
      return (
        form.sequence.length > 0 &&
        form.sequence.some(item => item.type !== 'wait')
      );
    case 3:
      if (form.audienceType === 'tags') return form.selectedAudience.length > 0;
      return form.selectedFunnel !== null && form.selectedStage !== null;
    case 4: {
      // Settings: validate schedule
      if (form.scheduleType === 'later' || form.scheduleType === 'recurring') {
        if (!form.scheduledAt) return false;
        if (new Date(form.scheduledAt) <= new Date()) return false;
      }
      if (form.scheduleType === 'recurring' && form.recurrenceType === 'cron') {
        if (!form.recurrenceCron.trim()) return false;
      }
      return true;
    }
    case 5:
      return true;
    default:
      return false;
  }
});

// Navigation
const goBack = () => {
  router.push({
    name: 'campaigns_whatsapp_index',
    params: { accountId: route.params.accountId },
  });
};

const nextStep = () => {
  if (currentStep.value < steps.value.length - 1) {
    currentStep.value += 1;
  }
};

const prevStep = () => {
  if (currentStep.value > 0) currentStep.value -= 1;
};

const toggleInbox = id => {
  const idx = form.selectedInboxIds.indexOf(id);
  if (idx >= 0) form.selectedInboxIds.splice(idx, 1);
  else form.selectedInboxIds.push(id);
};

const buildAudience = () => {
  if (form.audienceType === 'funnel') {
    return [
      {
        type: 'FunnelStage',
        funnel_id: form.selectedFunnel,
        stage_name: form.selectedStage,
      },
    ];
  }
  return form.selectedAudience.map(id => ({ type: 'Label', id }));
};

const collectAttachments = () =>
  form.sequence
    .filter(item => item.content?.blobId)
    .map(item => item.content.blobId);

// Date/time helpers
const toLocalDatetimeString = date => {
  const d = date instanceof Date ? date : new Date(date);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const defaultScheduleTime = () => {
  const now = new Date();
  now.setHours(now.getHours() + 1, 0, 0, 0);
  return toLocalDatetimeString(now);
};

const formatScheduleDisplay = val => {
  if (!val) return '-';
  const d = new Date(val);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 2000) return '-';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Load existing campaign data for edit mode
const loadCampaignForEdit = async campaignId => {
  isLoadingCampaign.value = true;
  try {
    // Try from store first, then fetch from API
    let campaign = store.state.campaigns.records.find(r => r.id === campaignId);
    if (!campaign) {
      await store.dispatch('campaigns/get');
      campaign = store.state.campaigns.records.find(r => r.id === campaignId);
    }
    if (!campaign) return;

    // Populate form
    form.title = campaign.title || '';
    form.description = campaign.description || '';

    // Parse message sequence
    if (campaign.message_sequence) {
      try {
        const seq =
          typeof campaign.message_sequence === 'string'
            ? JSON.parse(campaign.message_sequence)
            : campaign.message_sequence;
        if (Array.isArray(seq)) form.sequence = seq;
      } catch {
        // If sequence parsing fails, create text item from message
        if (campaign.message) {
          form.sequence = [
            { type: 'text', content: { text: campaign.message } },
          ];
        }
      }
    } else if (campaign.message) {
      form.sequence = [{ type: 'text', content: { text: campaign.message } }];
    }

    // Audience
    form.audienceType = campaign.audience_type || 'tags';
    if (form.audienceType === 'tags' && Array.isArray(campaign.audience)) {
      form.selectedAudience = campaign.audience
        .filter(a => a && a.type === 'Label' && a.id != null)
        .map(a => a.id);
    } else if (
      form.audienceType === 'funnel' &&
      Array.isArray(campaign.audience)
    ) {
      const funnelEntry = campaign.audience.find(
        a => a?.type === 'FunnelStage'
      );
      if (funnelEntry) {
        form.selectedFunnel = funnelEntry.funnel_id;
        form.selectedStage = funnelEntry.stage_name;
      }
    }

    // Inboxes
    if (Array.isArray(campaign.inbox_ids) && campaign.inbox_ids.length) {
      form.selectedInboxIds = [...campaign.inbox_ids];
    } else if (campaign.inbox_id) {
      form.selectedInboxIds = [campaign.inbox_id];
    } else if (campaign.inbox?.id) {
      form.selectedInboxIds = [campaign.inbox.id];
    }

    // Schedule — handle recurrence or one-time schedule
    if (campaign.recurrence_type) {
      form.scheduleType = 'recurring';
      form.recurrenceType = campaign.recurrence_type;
      if (campaign.recurrence_cron)
        form.recurrenceCron = campaign.recurrence_cron;
      if (campaign.scheduled_at) {
        const d = new Date(campaign.scheduled_at);
        if (!Number.isNaN(d.getTime()) && d.getFullYear() >= 2000) {
          form.scheduledAt = toLocalDatetimeString(d);
        }
      }
    } else if (campaign.scheduled_at) {
      const d = new Date(campaign.scheduled_at);
      if (!Number.isNaN(d.getTime()) && d.getFullYear() >= 2000) {
        form.scheduleType = 'later';
        form.scheduledAt = toLocalDatetimeString(d);
      }
    }

    // Anti-ban settings
    if (campaign.business_hours_only != null)
      form.businessHoursOnly = campaign.business_hours_only;
    if (campaign.business_hours_start != null)
      form.businessHoursStart = campaign.business_hours_start;
    if (campaign.business_hours_end != null)
      form.businessHoursEnd = campaign.business_hours_end;
    if (campaign.coffee_break_enabled != null)
      form.coffeeBreakEnabled = campaign.coffee_break_enabled;
    if (campaign.coffee_break_every != null)
      form.coffeeBreakEvery = campaign.coffee_break_every;
    if (campaign.max_messages_per_hour_per_inbox != null)
      form.maxMessagesPerHourPerInbox =
        campaign.max_messages_per_hour_per_inbox;
    if (campaign.min_delay_seconds != null)
      form.minDelaySeconds = campaign.min_delay_seconds;
    if (campaign.max_delay_seconds != null)
      form.maxDelaySeconds = campaign.max_delay_seconds;
    if (campaign.validate_numbers != null)
      form.validateNumbers = campaign.validate_numbers;
    if (campaign.track_links != null) form.trackLinks = campaign.track_links;

    // Target funnel
    if (campaign.target_funnel_id) {
      form.targetFunnelEnabled = true;
      form.targetFunnelId = campaign.target_funnel_id;
      form.targetFunnelStage = campaign.target_funnel_stage;
    }
  } finally {
    isLoadingCampaign.value = false;
  }
};

const extractTemplateParams = () => {
  const tmplItem = form.sequence.find(i => i.type === 'template');
  if (!tmplItem?.content?.templateName) return null;
  const { templateName, templateData, variables, headerImageUrl } =
    tmplItem.content;

  const processedParams = {
    body: Object.entries(variables || {}).reduce((acc, [key, val], idx) => {
      const cleanKey = key.replace(/[{}]/g, '');
      acc[cleanKey || String(idx + 1)] = val;
      return acc;
    }, {}),
  };

  // Add header image if template has IMAGE header
  const headerComp = (templateData?.components || []).find(
    c =>
      c.type?.toUpperCase() === 'HEADER' && c.format?.toUpperCase() === 'IMAGE'
  );
  if (headerComp) {
    const imageUrl =
      headerImageUrl ||
      headerComp.example?.header_handle?.[0] ||
      headerComp.example?.header_url?.[0] ||
      '';
    if (imageUrl) {
      processedParams.header = {
        media_url: imageUrl,
        media_type: 'image',
      };
    }
  }

  return {
    name: templateName,
    language: templateData?.language || 'pt_BR',
    category: templateData?.category || 'marketing',
    namespace: templateData?.namespace || '',
    components: templateData?.components || [],
    processed_params: processedParams,
  };
};

// Check if any selected inbox uses official Meta API (Cloud API or Notificame Hub)
const TEMPLATE_REQUIRED_PROVIDERS = ['whatsapp_cloud', 'notificame'];
const hasCloudApiInbox = computed(() =>
  selectedInboxObjects.value.some(inbox =>
    TEMPLATE_REQUIRED_PROVIDERS.includes(
      inbox.provider || inbox.additional_attributes?.provider
    )
  )
);

const buildCampaignPayload = () => ({
  campaign: {
    title: form.title,
    description: form.description,
    message: derivedMessage.value,
    message_sequence: JSON.stringify(form.sequence),
    inbox_id: form.selectedInboxIds[0],
    inbox_ids: form.selectedInboxIds,
    audience: buildAudience(),
    audience_type: form.audienceType,
    scheduled_at:
      (form.scheduleType === 'later' || form.scheduleType === 'recurring') &&
      form.scheduledAt
        ? new Date(form.scheduledAt).toISOString()
        : new Date().toISOString(),
    recurrence_type:
      form.scheduleType === 'recurring' ? form.recurrenceType : null,
    recurrence_cron:
      form.scheduleType === 'recurring' && form.recurrenceType === 'cron'
        ? form.recurrenceCron
        : null,
    // Anti-ban
    anti_ban_enabled: isMultiInbox.value,
    business_hours_only: form.businessHoursOnly,
    business_hours_start: form.businessHoursStart,
    business_hours_end: form.businessHoursEnd,
    coffee_break_enabled: form.coffeeBreakEnabled,
    coffee_break_every: form.coffeeBreakEvery,
    max_messages_per_hour_per_inbox: form.maxMessagesPerHourPerInbox,
    min_delay_seconds: form.minDelaySeconds,
    max_delay_seconds: form.maxDelaySeconds,
    // AI (derived from sequence)
    ai_enabled: aiFromSequence.value.enabled,
    ai_prompt: aiFromSequence.value.prompt || '',
    ai_system_prompt: aiFromSequence.value.systemPrompt || '',
    ai_tone: aiFromSequence.value.tone || 'friendly',
    ai_use_emojis: aiFromSequence.value.useEmojis ?? true,
    ai_include_greeting: aiFromSequence.value.includeGreeting ?? true,
    ai_max_length: aiFromSequence.value.maxLength || 500,
    // Validate numbers before sending
    validate_numbers: form.validateNumbers,
    // Encurta os links do texto para contar cliques (desligado por padrão)
    track_links: form.trackLinks,
    // Target funnel assignment
    target_funnel_id: form.targetFunnelEnabled ? form.targetFunnelId : null,
    target_funnel_stage: form.targetFunnelEnabled
      ? form.targetFunnelStage
      : null,
    // Template params for Cloud API / Notificame
    template_params: extractTemplateParams(),
    // Attachments from uploaded files in sequence
    attachments: collectAttachments(),
  },
});

const submitCampaign = async () => {
  isSubmitting.value = true;
  try {
    const data = buildCampaignPayload();
    if (isEditMode.value) {
      await store.dispatch('campaigns/update', {
        id: editCampaignId.value,
        ...data,
      });
    } else {
      await store.dispatch('campaigns/create', data);
    }
    showAlert(t('CAMPAIGN.WIZARD.SUCCESS'));
    goBack();
  } catch (err) {
    const serverMessage =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      t('CAMPAIGN.WIZARD.ERROR');
    showAlert(serverMessage);
  } finally {
    isSubmitting.value = false;
  }
};

// Test message
const showTestDialog = ref(false);
const testPhone = ref('');
const isSendingTest = ref(false);

const openTestDialog = () => {
  showTestDialog.value = true;
};

const closeTestDialog = () => {
  showTestDialog.value = false;
};

const sendTestMessage = async () => {
  if (!testPhone.value.trim()) return;

  // If campaign not saved yet, save as draft first
  if (!editCampaignId.value) {
    showAlert(t('CAMPAIGN.WIZARD.TEST_MESSAGE.SAVE_FIRST'));
    return;
  }

  isSendingTest.value = true;
  try {
    await CampaignsAPI.testMessage(editCampaignId.value, {
      phone_number: testPhone.value.trim(),
    });
    showAlert(t('CAMPAIGN.WIZARD.TEST_MESSAGE.SUCCESS'));
    closeTestDialog();
  } catch {
    showAlert(t('CAMPAIGN.WIZARD.TEST_MESSAGE.ERROR'));
  } finally {
    isSendingTest.value = false;
  }
};

// Watchers — skip resets while loading campaign data
watch(
  () => form.audienceType,
  () => {
    if (isLoadingCampaign.value) return;
    form.selectedAudience = [];
    form.selectedFunnel = null;
    form.selectedStage = null;
  }
);

watch(
  () => form.selectedFunnel,
  () => {
    if (isLoadingCampaign.value) return;
    form.selectedStage = null;
  }
);

watch(
  () => form.sendingSpeed,
  speed => {
    if (speed !== 'custom') updateDelaysFromSpeed(speed);
  }
);

watch(
  () => form.targetFunnelId,
  () => {
    if (isLoadingCampaign.value) return;
    form.targetFunnelStage = null;
  }
);

const currentDateTime = computed(() => toLocalDatetimeString(new Date()));

// Auto-refresh inboxes when entering step 1 (inboxes) if list is empty
watch(
  () => currentStep.value,
  step => {
    if (
      step === 1 &&
      (!whatsappInboxes.value || whatsappInboxes.value.length === 0)
    ) {
      store.dispatch('inboxes/get');
    }
  }
);

onMounted(async () => {
  store.dispatch('labels/get');
  store.dispatch('funnels/get');
  await store.dispatch('inboxes/get');
  if (editCampaignId.value) {
    await loadCampaignForEdit(editCampaignId.value);
  }
});
</script>

<template>
  <section class="flex flex-col h-full bg-n-background">
    <!-- Header -->
    <header class="px-4 sm:px-6 py-4 border-b border-n-weak">
      <div class="flex items-center gap-3">
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-arrow-left"
          @click="goBack"
        />
        <div>
          <h1 class="text-lg font-medium text-n-slate-12">
            {{
              isEditMode
                ? t('CAMPAIGN.WIZARD.EDIT_TITLE')
                : t('CAMPAIGN.WIZARD.TITLE')
            }}
          </h1>
          <p class="text-xs text-n-slate-10 mt-0.5">
            {{ steps[currentStep]?.label }}
          </p>
        </div>
      </div>
    </header>

    <!-- Step indicators -->
    <nav class="px-4 sm:px-6 py-3 border-b border-n-weak">
      <ol class="flex items-center gap-1">
        <li
          v-for="(step, index) in steps"
          :key="step.key"
          class="flex items-center gap-1"
        >
          <button
            class="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-all"
            :class="
              index < currentStep
                ? 'bg-n-teal-3 text-n-teal-11'
                : index === currentStep
                  ? 'bg-n-brand/10 text-n-brand'
                  : 'text-n-slate-10'
            "
            :disabled="index > currentStep"
            @click="index < currentStep ? (currentStep = index) : null"
          >
            <span
              v-if="index < currentStep"
              class="i-lucide-check w-3.5 h-3.5"
            />
            <span v-else :class="step.icon" class="w-3.5 h-3.5" />
            <span class="hidden sm:inline">{{ step.label }}</span>
            <span class="sm:hidden">{{ index + 1 }}</span>
          </button>
          <span
            v-if="index < steps.length - 1"
            class="i-lucide-chevron-right w-3 h-3 text-n-slate-8"
          />
        </li>
      </ol>
    </nav>

    <!-- Main content -->
    <main class="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
      <!-- Loading campaign data -->
      <div
        v-if="isLoadingCampaign"
        class="flex items-center justify-center py-20"
      >
        <Spinner />
      </div>
      <!-- ==================== STEP 0: BASIC INFO ==================== -->
      <div v-else-if="currentStep === 0" class="max-w-2xl mx-auto space-y-6">
        <div>
          <h2 class="text-base font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WIZARD.STEPS.BASIC') }}
          </h2>
          <p class="text-sm text-n-slate-10 mt-1">
            {{ t('CAMPAIGN.WIZARD.BASIC_DESCRIPTION') }}
          </p>
        </div>

        <Input
          v-model="form.title"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
        />

        <TextArea
          v-model="form.description"
          :label="t('CAMPAIGN.WIZARD.DESCRIPTION_LABEL')"
          :placeholder="t('CAMPAIGN.WIZARD.DESCRIPTION_PLACEHOLDER')"
          :rows="3"
        />
      </div>

      <!-- ==================== STEP 2: MESSAGE BUILDER ==================== -->
      <div v-if="currentStep === 2" class="max-w-7xl mx-auto flex gap-6">
        <div class="flex-1 min-w-0 space-y-5">
          <div>
            <h2 class="text-base font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WIZARD.STEPS.MESSAGE') }}
            </h2>
            <p class="text-sm text-n-slate-10 mt-1">
              {{ t('CAMPAIGN.WIZARD.MESSAGE_DESCRIPTION') }}
            </p>
          </div>

          <!-- Cloud API alert -->
          <div
            v-if="hasCloudApiInbox"
            class="flex items-start gap-2 p-3 rounded-lg bg-n-amber-3 border border-n-amber-6"
          >
            <span
              class="i-lucide-alert-triangle w-4 h-4 text-n-amber-11 mt-0.5 shrink-0"
            />
            <p class="text-xs text-n-amber-11">
              {{ t('CAMPAIGN.WIZARD.CLOUD_API_TEMPLATE_REQUIRED') }}
            </p>
          </div>

          <MessageSequenceBuilder
            :sequence="form.sequence"
            :account-id="accountId"
            :template-only="hasCloudApiInbox"
            @update:sequence="form.sequence = $event"
          />

          <!-- Test message button -->
          <div class="pt-3">
            <Button
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-send"
              @click="openTestDialog"
            >
              {{ t('CAMPAIGN.WIZARD.TEST_MESSAGE.BUTTON') }}
            </Button>
          </div>
        </div>

        <div
          class="hidden xl:flex flex-col items-center sticky top-0 pt-2 shrink-0"
        >
          <WhatsAppPhonePreview
            :sequence="form.sequence"
            :inbox-name="primaryInbox?.name || 'WhatsApp Business'"
          />
        </div>
      </div>

      <!-- Test message dialog -->
      <Teleport to="body">
        <div
          v-if="showTestDialog"
          class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black/50"
          @click.self="closeTestDialog"
        >
          <div
            class="bg-n-solid-1 rounded-xl border border-n-weak shadow-xl w-full max-w-md mx-4 p-6 space-y-4"
          >
            <div>
              <h3 class="text-base font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WIZARD.TEST_MESSAGE.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-10 mt-1">
                {{ t('CAMPAIGN.WIZARD.TEST_MESSAGE.DESCRIPTION') }}
              </p>
            </div>

            <Input
              v-model="testPhone"
              :label="t('CAMPAIGN.WIZARD.TEST_MESSAGE.PHONE_LABEL')"
              :placeholder="t('CAMPAIGN.WIZARD.TEST_MESSAGE.PHONE_PLACEHOLDER')"
              type="tel"
            />

            <div class="flex justify-end gap-2 pt-2">
              <Button
                variant="faded"
                color="slate"
                size="sm"
                @click="closeTestDialog"
              >
                {{ t('CAMPAIGN.WIZARD.TEST_MESSAGE.CANCEL') }}
              </Button>
              <Button
                size="sm"
                :disabled="!testPhone.trim() || isSendingTest"
                :loading="isSendingTest"
                @click="sendTestMessage"
              >
                <span class="i-lucide-send w-3.5 h-3.5 mr-1" />
                {{ t('CAMPAIGN.WIZARD.TEST_MESSAGE.SEND') }}
              </Button>
            </div>
          </div>
        </div>
      </Teleport>

      <!-- ==================== STEP 3: AUDIENCE ==================== -->
      <div v-if="currentStep === 3" class="max-w-2xl mx-auto space-y-5">
        <div>
          <h2 class="text-base font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WIZARD.STEPS.AUDIENCE') }}
          </h2>
          <p class="text-sm text-n-slate-10 mt-1">
            {{ t('CAMPAIGN.WIZARD.AUDIENCE_DESCRIPTION') }}
          </p>
        </div>

        <ComboBox
          v-model="form.audienceType"
          :options="audienceTypeOptions"
          :placeholder="
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE_TYPE.PLACEHOLDER')
          "
        />

        <!-- Tags -->
        <TagMultiSelectComboBox
          v-if="form.audienceType === 'tags'"
          v-model="form.selectedAudience"
          :options="labelOptions"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        />

        <!-- Funnel -->
        <template v-if="form.audienceType === 'funnel'">
          <ComboBox
            v-model="form.selectedFunnel"
            :options="funnelOptions"
            :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.FUNNEL.PLACEHOLDER')"
          />
          <ComboBox
            v-if="form.selectedFunnel"
            v-model="form.selectedStage"
            :options="stageOptions"
            :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.STAGE.PLACEHOLDER')"
          />
          <p v-if="form.selectedStage" class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.STAGE.INFO') }}
          </p>
        </template>

        <!-- Audience count badge -->
        <div
          v-if="audienceCount > 0"
          class="flex items-center gap-2 p-3 rounded-lg bg-n-teal-3"
        >
          <span class="i-lucide-users w-4 h-4 text-n-teal-11" />
          <span class="text-sm text-n-teal-11 font-medium">
            {{ audienceCount }}
            {{
              form.audienceType === 'tags'
                ? t('CAMPAIGN.WIZARD.TAGS_SELECTED')
                : t('CAMPAIGN.WIZARD.STAGE_SELECTED')
            }}
          </span>
        </div>
      </div>

      <!-- ==================== STEP 1: INBOXES ==================== -->
      <div v-if="currentStep === 1" class="max-w-3xl mx-auto space-y-6">
        <div>
          <h2 class="text-base font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WIZARD.STEPS.INBOXES') }}
          </h2>
          <p class="text-sm text-n-slate-10 mt-1">
            {{ t('CAMPAIGN.WIZARD.INBOXES_DESCRIPTION') }}
          </p>
        </div>

        <!-- Loading state -->
        <div
          v-if="isLoadingInboxes"
          class="flex flex-col items-center justify-center py-12"
        >
          <Spinner />
          <p class="mt-3 text-sm text-n-slate-10">
            {{ t('CAMPAIGN.WIZARD.LOADING_INBOXES') }}
          </p>
        </div>

        <!-- Inbox grid -->
        <div
          v-else-if="whatsappInboxes && whatsappInboxes.length > 0"
          class="grid grid-cols-1 sm:grid-cols-2 gap-3"
        >
          <button
            v-for="inbox in whatsappInboxes"
            :key="inbox.id"
            class="flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all"
            :class="
              form.selectedInboxIds.includes(inbox.id)
                ? 'border-n-brand bg-n-brand/5 shadow-sm'
                : 'border-n-weak hover:border-n-slate-6 hover:bg-n-alpha-1'
            "
            @click="toggleInbox(inbox.id)"
          >
            <div
              class="flex items-center justify-center w-5 h-5 rounded border-2 shrink-0 transition-all"
              :class="
                form.selectedInboxIds.includes(inbox.id)
                  ? 'border-n-brand bg-n-brand'
                  : 'border-n-slate-7'
              "
            >
              <span
                v-if="form.selectedInboxIds.includes(inbox.id)"
                class="i-lucide-check w-3 h-3 text-white"
              />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ inbox.name }}
              </p>
              <span
                class="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded mt-1"
                :class="getProviderColor(inbox)"
              >
                {{ getProviderLabel(inbox) }}
              </span>
            </div>
          </button>
        </div>

        <!-- Empty state -->
        <div v-else class="text-center py-8 text-n-slate-10">
          <span
            class="i-lucide-inbox w-10 h-10 block mx-auto mb-2 opacity-40"
          />
          <p class="text-sm">
            {{ t('CAMPAIGN.WIZARD.NO_WHATSAPP_INBOXES') }}
          </p>
          <p class="text-xs mt-1">
            {{ t('CAMPAIGN.WIZARD.NO_WHATSAPP_INBOXES_HINT') }}
          </p>
          <Button
            variant="faded"
            color="slate"
            size="sm"
            icon="i-lucide-refresh-cw"
            class="mt-3"
            @click="refreshInboxes"
          >
            {{ t('CAMPAIGN.REFRESH') }}
          </Button>
        </div>

        <!-- Multi-inbox anti-ban badge -->
        <div
          v-if="isMultiInbox"
          class="flex items-center gap-2 p-3 rounded-lg bg-n-teal-3"
        >
          <span class="i-lucide-shield-check w-4 h-4 text-n-teal-11" />
          <span class="text-xs text-n-teal-11 font-medium">
            {{ t('CAMPAIGN.WIZARD.MULTI_INBOX_ANTI_BAN') }}
          </span>
        </div>
      </div>

      <!-- ==================== STEP 4: SETTINGS (Anti-ban & Schedule) ==================== -->
      <div v-if="currentStep === 4" class="max-w-3xl mx-auto space-y-6">
        <div>
          <h2 class="text-base font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WIZARD.STEPS.SETTINGS') }}
          </h2>
          <p class="text-sm text-n-slate-10 mt-1">
            {{ t('CAMPAIGN.WIZARD.SETTINGS_DESCRIPTION') }}
          </p>
        </div>

        <!-- Target Funnel Assignment -->
        <div class="rounded-xl border border-n-weak p-5 space-y-4">
          <h3
            class="text-sm font-medium text-n-slate-12 flex items-center gap-2"
          >
            <span class="i-lucide-git-branch w-4 h-4 text-n-blue-11" />
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TARGET_FUNNEL.TITLE') }}
          </h3>

          <div class="flex items-center justify-between gap-4">
            <div class="flex flex-col">
              <span class="text-sm text-n-slate-12">
                {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TARGET_FUNNEL.ENABLE') }}
              </span>
              <span class="text-xs text-n-slate-10 mt-0.5">
                {{
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.TARGET_FUNNEL.ENABLE_DESC')
                }}
              </span>
            </div>
            <Switch
              :model-value="form.targetFunnelEnabled"
              @update:model-value="form.targetFunnelEnabled = $event"
            />
          </div>

          <template v-if="form.targetFunnelEnabled">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.TARGET_FUNNEL.FUNNEL_LABEL')
                }}
              </label>
              <ComboBox
                v-model="form.targetFunnelId"
                :options="funnelOptions"
                :placeholder="
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.TARGET_FUNNEL.FUNNEL_PLACEHOLDER'
                  )
                "
                class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak"
              />
            </div>

            <div v-if="form.targetFunnelId" class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.TARGET_FUNNEL.STAGE_LABEL')
                }}
              </label>
              <ComboBox
                v-model="form.targetFunnelStage"
                :options="targetStageOptions"
                :placeholder="
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.TARGET_FUNNEL.STAGE_PLACEHOLDER'
                  )
                "
                class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak"
              />
              <span class="text-xs text-n-slate-10">
                {{
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.TARGET_FUNNEL.STAGE_INFO')
                }}
              </span>
            </div>
          </template>
        </div>

        <!-- Validate numbers -->
        <div class="rounded-xl border border-n-weak p-5">
          <div class="flex items-center justify-between gap-4">
            <div class="flex items-center gap-2">
              <span class="i-lucide-phone-call w-4 h-4 text-n-teal-11" />
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WIZARD.VALIDATE_NUMBERS') }}
              </span>
            </div>
            <Switch
              :model-value="form.validateNumbers"
              @update:model-value="form.validateNumbers = $event"
            />
          </div>
          <p class="text-xs text-n-slate-10 mt-2">
            {{ t('CAMPAIGN.WIZARD.VALIDATE_NUMBERS_DESC') }}
          </p>
        </div>

        <!-- Rastrear cliques nos links -->
        <div class="rounded-xl border border-n-weak p-5">
          <div class="flex items-center justify-between gap-4">
            <div class="flex items-center gap-2">
              <span class="i-lucide-link w-4 h-4 text-n-teal-11" />
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WIZARD.TRACK_LINKS') }}
              </span>
            </div>
            <Switch
              :model-value="form.trackLinks"
              @update:model-value="form.trackLinks = $event"
            />
          </div>
          <p class="text-xs text-n-slate-10 mt-2">
            {{ t('CAMPAIGN.WIZARD.TRACK_LINKS_DESC') }}
          </p>
        </div>

        <!-- Anti-ban settings -->
        <div class="rounded-xl border border-n-weak p-5 space-y-5">
          <h3
            class="text-sm font-medium text-n-slate-12 flex items-center gap-2"
          >
            <span class="i-lucide-shield w-4 h-4 text-n-blue-11" />
            {{ t('CAMPAIGN.WIZARD.ANTI_BAN.TITLE') }}
          </h3>

          <!-- Sending Speed -->
          <div class="space-y-2">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WIZARD.ANTI_BAN.SPEED') }}
            </label>
            <div class="grid grid-cols-2 gap-2">
              <button
                v-for="opt in sendingSpeedOptions"
                :key="opt.value"
                type="button"
                class="flex flex-col p-3 rounded-lg border-2 text-left transition-all"
                :class="
                  form.sendingSpeed === opt.value
                    ? 'bg-n-brand/5 border-n-brand'
                    : 'bg-n-alpha-1 border-n-weak hover:border-n-slate-6'
                "
                @click="form.sendingSpeed = opt.value"
              >
                <div class="flex items-center gap-1.5">
                  <span :class="opt.icon" class="w-3.5 h-3.5 text-n-slate-11" />
                  <span
                    class="text-sm font-medium"
                    :class="
                      form.sendingSpeed === opt.value
                        ? 'text-n-brand'
                        : 'text-n-slate-12'
                    "
                  >
                    {{ opt.label }}
                  </span>
                </div>
                <span class="text-[11px] text-n-slate-10 mt-0.5 ml-5">
                  {{ opt.desc }}
                </span>
              </button>
            </div>
          </div>

          <!-- Custom delay -->
          <div
            v-if="form.sendingSpeed === 'custom'"
            class="grid grid-cols-2 gap-3"
          >
            <Input
              v-model.number="form.minDelaySeconds"
              :label="t('CAMPAIGN.WIZARD.ANTI_BAN.DELAY') + ' min (s)'"
              type="number"
              min="1"
              max="300"
            />
            <Input
              v-model.number="form.maxDelaySeconds"
              :label="t('CAMPAIGN.WIZARD.ANTI_BAN.DELAY') + ' max (s)'"
              type="number"
              min="1"
              max="600"
            />
          </div>

          <!-- Business hours -->
          <div class="p-3 rounded-lg bg-n-alpha-2 space-y-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="i-lucide-clock w-4 h-4 text-n-slate-11" />
                <span class="text-sm text-n-slate-12">
                  {{ t('CAMPAIGN.WIZARD.ANTI_BAN.BUSINESS_HOURS') }}
                </span>
              </div>
              <Switch
                :model-value="form.businessHoursOnly"
                @update:model-value="form.businessHoursOnly = $event"
              />
            </div>
            <div v-if="form.businessHoursOnly" class="grid grid-cols-2 gap-3">
              <Input
                v-model.number="form.businessHoursStart"
                :label="t('CAMPAIGN.WIZARD.ANTI_BAN.FROM')"
                type="number"
                min="0"
                max="23"
              />
              <Input
                v-model.number="form.businessHoursEnd"
                :label="t('CAMPAIGN.WIZARD.ANTI_BAN.TO')"
                type="number"
                min="0"
                max="23"
              />
            </div>
          </div>

          <!-- Allowed days -->
          <div class="p-3 rounded-lg bg-n-alpha-2 space-y-3">
            <label class="text-sm text-n-slate-12 flex items-center gap-2">
              <span class="i-lucide-calendar-days w-4 h-4 text-n-slate-11" />
              {{ t('CAMPAIGN.WIZARD.ANTI_BAN.ALLOWED_DAYS') }}
            </label>
            <div class="flex gap-2 flex-wrap">
              <button
                v-for="day in daysOfWeek"
                :key="day.value"
                type="button"
                class="px-3 py-1.5 text-xs font-medium rounded-lg border-2 transition-all"
                :class="
                  form.allowedDays.includes(day.value)
                    ? 'bg-n-brand border-n-brand text-white'
                    : 'bg-n-alpha-1 border-n-weak text-n-slate-11 hover:border-n-slate-6'
                "
                @click="toggleDay(day.value)"
              >
                {{ day.label }}
              </button>
            </div>
          </div>

          <!-- Coffee break -->
          <div class="p-3 rounded-lg bg-n-alpha-2 space-y-2">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="i-lucide-coffee w-4 h-4 text-n-slate-11" />
                <span class="text-sm text-n-slate-12">
                  {{ t('CAMPAIGN.WIZARD.ANTI_BAN.COFFEE_BREAK') }}
                </span>
              </div>
              <Switch
                :model-value="form.coffeeBreakEnabled"
                @update:model-value="form.coffeeBreakEnabled = $event"
              />
            </div>
            <p class="text-xs text-n-slate-10 ml-6">
              {{ t('CAMPAIGN.WIZARD.ANTI_BAN.COFFEE_BREAK_DESC') }}
            </p>
          </div>
        </div>

        <!-- Schedule -->
        <div class="rounded-xl border border-n-weak p-5 space-y-4">
          <h3
            class="text-sm font-medium text-n-slate-12 flex items-center gap-2"
          >
            <span class="i-lucide-calendar w-4 h-4 text-n-blue-11" />
            {{ t('CAMPAIGN.WIZARD.SCHEDULE.TITLE') }}
          </h3>
          <div class="flex gap-3">
            <button
              class="flex-1 p-3.5 rounded-lg border-2 text-center text-sm transition-all"
              :class="
                form.scheduleType === 'now'
                  ? 'border-n-brand bg-n-brand/5 text-n-slate-12 font-medium'
                  : 'border-n-weak text-n-slate-10 hover:border-n-slate-6'
              "
              @click="form.scheduleType = 'now'"
            >
              <span class="i-lucide-zap w-5 h-5 block mx-auto mb-1.5" />
              {{ t('CAMPAIGN.WIZARD.SCHEDULE.NOW') }}
            </button>
            <button
              class="flex-1 p-3.5 rounded-lg border-2 text-center text-sm transition-all"
              :class="
                form.scheduleType === 'later'
                  ? 'border-n-brand bg-n-brand/5 text-n-slate-12 font-medium'
                  : 'border-n-weak text-n-slate-10 hover:border-n-slate-6'
              "
              @click="
                form.scheduleType = 'later';
                if (!form.scheduledAt) form.scheduledAt = defaultScheduleTime();
              "
            >
              <span
                class="i-lucide-calendar-clock w-5 h-5 block mx-auto mb-1.5"
              />
              {{ t('CAMPAIGN.WIZARD.SCHEDULE.LATER') }}
            </button>
            <button
              class="flex-1 p-3.5 rounded-lg border-2 text-center text-sm transition-all"
              :class="
                form.scheduleType === 'recurring'
                  ? 'border-n-brand bg-n-brand/5 text-n-slate-12 font-medium'
                  : 'border-n-weak text-n-slate-10 hover:border-n-slate-6'
              "
              @click="
                form.scheduleType = 'recurring';
                if (!form.recurrenceType) form.recurrenceType = 'daily';
                if (!form.scheduledAt) form.scheduledAt = defaultScheduleTime();
              "
            >
              <span class="i-lucide-repeat w-5 h-5 block mx-auto mb-1.5" />
              {{ t('CAMPAIGN.WIZARD.SCHEDULE.RECURRING') }}
            </button>
          </div>
          <!-- Recurrence type selector -->
          <div v-if="form.scheduleType === 'recurring'" class="space-y-3">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WIZARD.SCHEDULE.RECURRENCE_TYPE') }}
            </label>
            <div class="flex gap-2">
              <button
                v-for="rType in ['daily', 'weekly', 'monthly', 'cron']"
                :key="rType"
                class="flex-1 p-2.5 rounded-lg border-2 text-center text-sm transition-all"
                :class="
                  form.recurrenceType === rType
                    ? 'border-n-brand bg-n-brand/5 text-n-slate-12 font-medium'
                    : 'border-n-weak text-n-slate-10 hover:border-n-slate-6'
                "
                @click="form.recurrenceType = rType"
              >
                {{ t(`CAMPAIGN.WIZARD.SCHEDULE.${rType.toUpperCase()}`) }}
              </button>
            </div>
            <p class="text-xs text-n-slate-10">
              {{
                form.recurrenceType
                  ? t(
                      `CAMPAIGN.WIZARD.SCHEDULE.${form.recurrenceType.toUpperCase()}_DESC`
                    )
                  : ''
              }}
            </p>
            <Input
              v-if="form.recurrenceType === 'cron'"
              v-model="form.recurrenceCron"
              :label="t('CAMPAIGN.WIZARD.SCHEDULE.CRON_LABEL')"
              :placeholder="t('CAMPAIGN.WIZARD.SCHEDULE.CRON_PLACEHOLDER')"
            />
            <Input
              v-model="form.scheduledAt"
              :label="t('CAMPAIGN.WIZARD.SCHEDULE.FIRST_RUN')"
              type="datetime-local"
              :min="currentDateTime"
            />
            <p
              v-if="form.scheduledAt"
              class="text-xs text-n-slate-10 flex items-center gap-1.5"
            >
              <span class="i-lucide-clock w-3 h-3" />
              {{ formatScheduleDisplay(form.scheduledAt) }}
            </p>
          </div>
          <div v-if="form.scheduleType === 'later'" class="space-y-2">
            <Input
              v-model="form.scheduledAt"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
              type="datetime-local"
              :min="currentDateTime"
            />
            <p
              v-if="form.scheduledAt"
              class="text-xs text-n-slate-10 flex items-center gap-1.5"
            >
              <span class="i-lucide-clock w-3 h-3" />
              {{ formatScheduleDisplay(form.scheduledAt) }}
            </p>
          </div>
        </div>
      </div>

      <!-- ==================== STEP 5: REVIEW ==================== -->
      <div v-if="currentStep === 5" class="max-w-5xl mx-auto flex gap-8">
        <div class="flex-1 min-w-0 space-y-6">
          <div>
            <h2 class="text-base font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WIZARD.STEPS.REVIEW') }}
            </h2>
          </div>

          <!-- Summary card -->
          <div class="rounded-xl border border-n-weak overflow-hidden">
            <div class="px-4 py-3 bg-n-alpha-2 border-b border-n-weak">
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WIZARD.REVIEW_SUMMARY') }}
              </h3>
            </div>
            <dl class="divide-y divide-n-weak">
              <div class="flex justify-between px-4 py-3">
                <dt class="text-sm text-n-slate-10">
                  {{ t('CAMPAIGN.WIZARD.REVIEW_TITLE') }}
                </dt>
                <dd class="text-sm text-n-slate-12 font-medium">
                  {{ form.title }}
                </dd>
              </div>
              <div
                v-if="form.description"
                class="flex justify-between px-4 py-3"
              >
                <dt class="text-sm text-n-slate-10">
                  {{ t('CAMPAIGN.WIZARD.REVIEW_DESCRIPTION') }}
                </dt>
                <dd class="text-sm text-n-slate-12">
                  {{ form.description }}
                </dd>
              </div>
              <div class="flex justify-between px-4 py-3">
                <dt class="text-sm text-n-slate-10">
                  {{ t('CAMPAIGN.WIZARD.REVIEW_MESSAGES') }}
                </dt>
                <dd class="text-sm text-n-slate-12">
                  {{ messageCount }}
                  {{ messageCount === 1 ? 'item' : 'itens' }}
                </dd>
              </div>
              <div class="flex justify-between px-4 py-3">
                <dt class="text-sm text-n-slate-10">
                  {{ t('CAMPAIGN.WIZARD.REVIEW_INBOXES') }}
                </dt>
                <dd class="text-sm text-n-slate-12">
                  {{ selectedInboxObjects.map(i => i.name).join(', ') || '-' }}
                </dd>
              </div>
              <div class="flex justify-between px-4 py-3">
                <dt class="text-sm text-n-slate-10">
                  {{ t('CAMPAIGN.WIZARD.REVIEW_AUDIENCE') }}
                </dt>
                <dd class="text-sm text-n-slate-12">
                  {{ audienceCount }}
                  {{ t('CAMPAIGN.WIZARD.REVIEW_CONTACTS') }}
                </dd>
              </div>
              <div class="flex justify-between px-4 py-3">
                <dt class="text-sm text-n-slate-10">
                  {{ t('CAMPAIGN.WIZARD.ANTI_BAN.SPEED') }}
                </dt>
                <dd class="text-sm text-n-slate-12">
                  {{
                    sendingSpeedOptions.find(o => o.value === form.sendingSpeed)
                      ?.label
                  }}
                </dd>
              </div>
              <div class="flex justify-between px-4 py-3">
                <dt class="text-sm text-n-slate-10">
                  {{ t('CAMPAIGN.WIZARD.REVIEW_AI') }}
                </dt>
                <dd class="text-sm text-n-slate-12">
                  {{
                    aiFromSequence.enabled
                      ? t('CAMPAIGN.WIZARD.ENABLED')
                      : t('CAMPAIGN.WIZARD.DISABLED')
                  }}
                </dd>
              </div>
              <div v-if="isMultiInbox" class="flex justify-between px-4 py-3">
                <dt class="text-sm text-n-slate-10">
                  {{ t('CAMPAIGN.WIZARD.REVIEW_ANTI_BAN') }}
                </dt>
                <dd class="text-sm text-n-teal-11 font-medium">
                  {{ t('CAMPAIGN.WIZARD.ENABLED') }}
                </dd>
              </div>
              <div class="flex justify-between px-4 py-3">
                <dt class="text-sm text-n-slate-10">
                  {{ t('CAMPAIGN.WIZARD.REVIEW_SCHEDULE') }}
                </dt>
                <dd class="text-sm text-n-slate-12">
                  <template v-if="form.scheduleType === 'now'">
                    {{ t('CAMPAIGN.WIZARD.SCHEDULE.NOW') }}
                  </template>
                  <template v-else-if="form.scheduleType === 'recurring'">
                    <span class="inline-flex items-center gap-1.5">
                      <span class="i-lucide-repeat w-3.5 h-3.5 text-n-brand" />
                      {{
                        t(
                          `CAMPAIGN.WIZARD.SCHEDULE.${form.recurrenceType?.toUpperCase() || 'DAILY'}`
                        )
                      }}
                      &mdash;
                      {{ t('CAMPAIGN.WIZARD.SCHEDULE.FIRST_RUN') }}:
                      {{ formatScheduleDisplay(form.scheduledAt) }}
                    </span>
                  </template>
                  <template v-else>
                    {{ formatScheduleDisplay(form.scheduledAt) }}
                  </template>
                </dd>
              </div>
            </dl>
          </div>
        </div>

        <div
          class="hidden lg:flex flex-col items-center sticky top-0 pt-2 shrink-0"
        >
          <WhatsAppPhonePreview
            :sequence="form.sequence"
            :inbox-name="primaryInbox?.name || 'WhatsApp Business'"
          />
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="px-4 sm:px-6 py-4 border-t border-n-weak">
      <div class="flex justify-between max-w-5xl mx-auto">
        <Button
          v-if="currentStep > 0"
          variant="faded"
          color="slate"
          icon="i-lucide-arrow-left"
          @click="prevStep"
        >
          {{ t('CAMPAIGN.WIZARD.PREVIOUS') }}
        </Button>
        <div v-else />

        <div class="flex gap-2">
          <Button variant="faded" color="slate" @click="goBack">
            {{ t('CAMPAIGN.WIZARD.CANCEL') }}
          </Button>
          <Button
            v-if="currentStep < steps.length - 1"
            :disabled="!canProceed"
            @click="nextStep"
          >
            {{ t('CAMPAIGN.WIZARD.NEXT') }}
            <span class="i-lucide-arrow-right w-4 h-4 ml-1" />
          </Button>
          <Button
            v-else
            :disabled="!canProceed || isSubmitting"
            :loading="isSubmitting"
            @click="submitCampaign"
          >
            <span
              class="w-4 h-4 mr-1"
              :class="isEditMode ? 'i-lucide-save' : 'i-lucide-send'"
            />
            {{
              isEditMode ? t('CAMPAIGN.WIZARD.SAVE') : t('CAMPAIGN.WIZARD.SEND')
            }}
          </Button>
        </div>
      </div>
    </footer>
  </section>
</template>
